import { castArray, merge } from 'lodash';
import { BehaviorSubject, NEVER, of } from 'rxjs';
import { tap, map } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { ISnListQueryParameters } from '@lib/api';
import {
  SnCameraType,
  SnPullProtocol,
  SnCameraProtocol,
  SnCameraControlType,
  SnCameraDisposition,
  SnCameraUnmanagedType,
  SnCameraManagementType,
  SnAppliance,
  SnExternalAppliance,
  SnCamera,
  SnLiveApplication,
  SnCameraPtzMovementType,
  FsAuthDigest,
} from '@lib/api/entities';
import {
  SnAppliancesAPI,
  SnCameraGroupsAPI,
  SnCamerasAPI,
  SnExternalAppliancesAPI,
} from '@lib/api/services';
import { ISnApplianceSelectFieldOptions } from '@lib/appliances';
import { NETWORK_PROTOCOL_SPECS } from '@lib/common';
import { SnFormFieldConfig } from '@lib/form';
import { ISnAutocompleteFormFieldConfig } from '@lib/form/autocomplete';
import { ISnGeometryFormFieldConfig } from '@lib/form/geometry';
import { ISnHttpSelectFormFieldConfig } from '@lib/form/http-select';
import { ISnInputFormFieldConfig } from '@lib/form/input';
import { ISnRadialSliderFormFieldConfig } from '@lib/form/radial-slider';
import { ISnSelectFormFieldConfig } from '@lib/form/select';
import { ISnSliderFormFieldConfig } from '@lib/form/slider';
import { ISnSelectSearchFormFieldConfig } from '@lib/select-search';
import { t } from '@lib/translation';

import { AddCameraGroupDialogComponent } from '../components/add-camera-group-dialog/add-camera-group-dialog.component';

@Injectable({ providedIn: 'root' })
export class SnCameraFieldsGeneratorService {
  constructor(
    private readonly _camerasAPI: SnCamerasAPI,
    private readonly _cameraGroupsAPI: SnCameraGroupsAPI,
    private readonly _appliancesAPI: SnAppliancesAPI,
    private readonly _externalAppliancesAPI: SnExternalAppliancesAPI,
  ) {}

  rtspPathOptions = new BehaviorSubject<any[]>([]);

  public getLiveApplicationSelectField(
    applianceControlName: string = 'appliance',
    fieldConfig?: Partial<ISnHttpSelectFormFieldConfig<SnLiveApplication>>,
  ): ISnHttpSelectFormFieldConfig<SnLiveApplication> {
    const baseFieldConfig: ISnHttpSelectFormFieldConfig<SnLiveApplication> = {
      key: 'liveApp',
      type: 'http-select',
      templateOptions: {
        label: t('cameras.liveApplication'),
        fetchDataFn: (params: ISnListQueryParameters, model, field) => {
          const applianceId: string = field.parent.formControl.get(applianceControlName)?.value;
          const applications$ = this._camerasAPI.getLiveApplicationList(applianceId, params).pipe(
            tap((applications) => {
              if (applications.count === 1) {
                field.formControl.patchValue(applications.results[0].id);
              }
            }),
          );

          return applianceId ? applications$ : NEVER;
        },
        valueProp: 'id',
        labelProp: 'name',
        infinityScroll: true,
      },
      expressionProperties: {
        'templateOptions.disabled': (model, formState, field) => {
          const applianceControl = field.parent.formControl.get(applianceControlName);

          if (applianceControl) {
            const aplianceId = applianceControl.value;
            return !aplianceId;
          }

          return true;
        },
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getNameField(fieldConfig: SnFormFieldConfig = {}): SnFormFieldConfig {
    const baseFieldConfig = {
      key: 'name',
      type: 'input',
      className: 'mt-xs',
      templateOptions: {
        label: t('common.name'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getCameraTypeField(
    fieldConfig?: Partial<ISnHttpSelectFormFieldConfig<SnCameraType>>,
  ): ISnHttpSelectFormFieldConfig<SnCameraType> {
    const baseFieldConfig: ISnHttpSelectFormFieldConfig<SnCameraType> = {
      key: 'cameraType',
      type: 'http-select',
      templateOptions: {
        label: t('common.type'),
        infinityScroll: true,
        valueProp: 'id',
        labelProp: 'name',
        fetchDataFn: (params: any) => this._camerasAPI.getCameraTypeList(params),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getCamerasCoreField(
    fieldConfig?: Partial<ISnHttpSelectFormFieldConfig<SnAppliance>>,
  ): ISnHttpSelectFormFieldConfig<SnAppliance> {
    const baseFieldConfig: ISnHttpSelectFormFieldConfig<SnAppliance> = {
      key: 'camerasCore',
      type: 'http-select',
      templateOptions: {
        label: t('common.appliance'),
        floatLabel: 'always',
        placeholder: t('common.none'),
        infinityScroll: true,
        valueProp: 'id',
        labelProp: 'name',
        fetchDataFn: (params: any) => this._appliancesAPI.getApplianceList(params),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getParentCameraField(
    childCameraId: string,
    fieldConfig?: Partial<ISnHttpSelectFormFieldConfig<SnCamera>>,
  ): ISnHttpSelectFormFieldConfig<SnCamera> {
    const baseFieldConfig: ISnHttpSelectFormFieldConfig<SnCamera> = {
      key: 'parentCamera',
      type: 'http-select',
      templateOptions: {
        label: t('cameras.parentCamera'),
        infinityScroll: true,
        valueProp: 'id',
        labelProp: 'name',
        search: true,
        fetchDataFn: (params: any) =>
          this._camerasAPI.getCameraList(params).pipe(
            map((response) => {
              return {
                count: response.count,
                totalPages: response.totalPages,
                results: response.results
                  .map((camera) => new SnCamera(camera))
                  .filter((cam) => cam.id !== childCameraId),
              };
            }),
          ),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getIsPublicField(fieldConfig: SnFormFieldConfig = {}): SnFormFieldConfig {
    const baseFieldConfig = {
      key: 'isPublic',
      type: 'drag-slide-toggle',
      className: 'mt-xs',
      templateOptions: {
        appearance: 'fill',
        toggleLabel: t('cameras.public'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getMakeField(fieldConfig: SnFormFieldConfig = {}): SnFormFieldConfig {
    const baseFieldConfig = {
      key: 'make',
      type: 'input',
      className: 'mt-xs',
      templateOptions: {
        label: t('cameras.table.columns.make'),
        placeholder: t('cameras.enterMake'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getModelField(fieldConfig: SnFormFieldConfig = {}): SnFormFieldConfig {
    const baseFieldConfig = {
      key: 'model',
      type: 'input',
      className: 'mt-xs',
      templateOptions: {
        label: t('cameras.table.columns.model'),
        placeholder: t('cameras.enterModel'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getTypeField(fieldConfig: SnFormFieldConfig = {}): SnFormFieldConfig {
    const baseFieldConfig = {
      key: 'type',
      type: 'radio',
      className: 'mt-xs',
      templateOptions: {
        appearance: 'fill',
        label: t('common.control'),
        options: [
          { label: t('cameras.form.options.fixed'), value: SnCameraControlType.Fixed },
          { label: NETWORK_PROTOCOL_SPECS.DEVICE_CONTROLS.PTZ, value: SnCameraControlType.PTZ },
        ],
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getPtzControlInvertX(fieldConfig: SnFormFieldConfig = {}): SnFormFieldConfig {
    const baseFieldConfig = {
      key: 'ptzControlInvertX',
      type: 'drag-slide-toggle',
      templateOptions: {
        toggleLabel: t('cameras.invertXAxis'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getPtzControlInvertY(fieldConfig: SnFormFieldConfig = {}): SnFormFieldConfig {
    const baseFieldConfig = {
      key: 'ptzControlInvertY',
      type: 'drag-slide-toggle',
      templateOptions: {
        toggleLabel: t('cameras.invertYAxis'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getPtzEnableArrowsField(fieldConfig?: Partial<SnFormFieldConfig>): SnFormFieldConfig {
    const baseFieldConfig: SnFormFieldConfig = {
      key: 'ptzControlEnableArrows',
      type: 'drag-slide-toggle',
      templateOptions: {
        toggleLabel: t('cameras.enableArrowKeyControls'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getPtzZoomField(fieldConfig: SnFormFieldConfig = {}): SnFormFieldConfig {
    const baseFieldConfig = {
      key: 'ptzControlZoomOnly',
      type: 'drag-slide-toggle',
      templateOptions: {
        toggleLabel: t('cameras.zoomOnlyPtz'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getPtzControlMovementTypeField(fieldConfig: SnFormFieldConfig = {}): SnFormFieldConfig {
    const baseFieldConfig = {
      key: 'ptzMovementType',
      type: 'radio',
      templateOptions: {
        appearance: 'fill',
        label: t('cameras.ptzControlMovementType'),
        options: [
          { label: t('cameras.relative'), value: SnCameraPtzMovementType.RELATIVE },
          { label: t('cameras.continuous'), value: SnCameraPtzMovementType.CONTINUOUS },
        ],
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getGroupsField(
    fieldConfig: SnFormFieldConfig = {},
    setParams?: ISnListQueryParameters,
  ): SnFormFieldConfig {
    const baseFieldConfig = {
      key: 'groups',
      type: 'http-select',
      templateOptions: {
        multiple: true,
        infinityScroll: true,
        valueProp: 'id',
        labelProp: 'name',
        label: t('cameras.groups'),
        search: true,
        addComponent: AddCameraGroupDialogComponent,
        fetchDataFn: (params: ISnListQueryParameters) =>
          this._cameraGroupsAPI.getList({ ...params, ...setParams }),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getDispositionField(fieldConfig: SnFormFieldConfig = {}): SnFormFieldConfig {
    const baseFieldConfig = {
      key: 'disposition',
      type: 'radio',
      className: 'mt-xs',
      templateOptions: {
        appearance: 'fill',
        label: t('common.disposition'),
        options: [
          { label: t('common.external'), value: SnCameraDisposition.External },
          { label: t('common.internal'), value: SnCameraDisposition.Internal },
        ],
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getGeometryField(
    fieldConfig?: Partial<ISnGeometryFormFieldConfig>,
  ): ISnGeometryFormFieldConfig {
    const baseFieldConfig = {
      key: 'geometry',
      type: 'geometry',
      templateOptions: {
        mapOptions: {
          zoom: 18,
        },
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getRadialField(
    fieldConfig?: Partial<ISnRadialSliderFormFieldConfig>,
  ): ISnRadialSliderFormFieldConfig {
    const baseFieldConfig = {
      key: 'heading',
      type: 'radial',
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getManagementTypeField(fieldConfig?: Partial<SnFormFieldConfig>): SnFormFieldConfig {
    const baseFieldConfig: SnFormFieldConfig = {
      key: 'managementType',
      type: 'radio',
      defaultValue: SnCameraManagementType.Managed,
      templateOptions: {
        appearance: 'fill',
        options: [
          { label: t('cameras.form.options.fususCore'), value: SnCameraManagementType.Managed },
          { label: t('common.directConnect'), value: SnCameraManagementType.Unmanaged },
          { label: t('cameras.form.options.externalAppliance'), value: SnCameraManagementType.External },
        ],
      },
      wrappers: ['form-field'],
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getIdleTimeField(fieldConfig?: Partial<ISnInputFormFieldConfig>): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'idleTime',
      type: 'input',
      templateOptions: {
        type: 'number',
        label: t('cameras.idleTime'),
        placeholder: t('cameras.enterIdleTime'),
        min: 0,
      },
      validation: {
        messages: {
          min: t('cameras.idleTimeValidation'),
        },
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getHostField(fieldConfig?: Partial<ISnInputFormFieldConfig>): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'host',
      type: 'input',
      templateOptions: {
        label: t('cameras.form.fields.hostIpAddress'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getPortField(fieldConfig?: Partial<ISnInputFormFieldConfig>): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'port',
      type: 'input',
      templateOptions: {
        label: t('common.port'),
        placeholder: '0000',
      },
      validators: {
        validation: ['port'],
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getUsernameField(fieldConfig?: Partial<ISnInputFormFieldConfig>): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'username',
      type: 'input',
      templateOptions: {
        label: t('common.username'),
        placeholder: t('cameras.enterUsername'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getPasswordField(fieldConfig?: Partial<ISnInputFormFieldConfig>): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'password',
      type: 'input',
      templateOptions: {
        type: 'password',
        label: t('common.password'),
        placeholder: t('cameras.enterPassword'),
        attributes: {
          autocomplete: 'new-password',
        },
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getPathField(
    fieldConfig?: Partial<ISnAutocompleteFormFieldConfig>,
  ): ISnAutocompleteFormFieldConfig {
    const baseFieldConfig: ISnAutocompleteFormFieldConfig = {
      key: 'path',
      type: 'autocomplete',
      templateOptions: {
        label: t('cameras.path'),
        placeholder: t('cameras.selectEnterPath'),
        fetchFn: (value?: string) => {
          const data = this.rtspPathOptions.value;
          return value == null
            ? of(data)
            : of(data.filter((item) => item.label.toLowerCase().includes(value.toLowerCase())));
        },
        availableOptions: this.rtspPathOptions,
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public updateRtspPaths(formData): void {
    const make = formData.make;
    if (!make || make.trim() === '') {
      this.rtspPathOptions.next([]);
      return;
    }
    const params = { make__icontains: make, model__icontains: formData.model, only_path: true };
    this._camerasAPI
      .getRtspPathRecommendations(params)
      .subscribe((response: { results: any[]; count: number; totalPages: number }) => {
        const data = response.results.map((item) => ({
          value: item.path,
          label: item.path,
        }));
        this.rtspPathOptions.next(data);
      });
  }

  public getAIPathExtField(
    fieldConfig?: Partial<ISnInputFormFieldConfig>,
  ): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'aiPathExt',
      type: 'input',
      templateOptions: {
        placeholder: t('cameras.aiExternalPath'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getSubPathField(
    fieldConfig?: Partial<ISnAutocompleteFormFieldConfig>,
  ): ISnAutocompleteFormFieldConfig {
    const baseFieldConfig: ISnAutocompleteFormFieldConfig = {
      key: 'pathSub',
      type: 'autocomplete',
      templateOptions: {
        label: t('cameras.secondaryPath'),
        placeholder: t('cameras.selectEnterSecondaryPath'),
        fetchFn: (value?: string) => {
          const data = this.rtspPathOptions.value;
          return value == null
            ? of(data)
            : of(data.filter((item) => item.label.toLowerCase().includes(value.toLowerCase())));
        },
        availableOptions: this.rtspPathOptions,
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getProtocolField(fieldConfig?: Partial<SnFormFieldConfig>): SnFormFieldConfig {
    const baseFieldConfig: SnFormFieldConfig = {
      key: 'protocol',
      type: 'radio',
      defaultValue: SnPullProtocol.RTSP,
      templateOptions: {
        appearance: 'fill',
        label: t('cameras.protocol'),
        options: [
          { label: NETWORK_PROTOCOL_SPECS.PROTOCOLS.RTSP, value: SnPullProtocol.RTSP },
          { label: NETWORK_PROTOCOL_SPECS.PROTOCOLS.RTMP, value: SnPullProtocol.RTMP },
        ],
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getAuthDigestField(fieldConfig?: Partial<SnFormFieldConfig>): SnFormFieldConfig {
    const baseFieldConfig: SnFormFieldConfig = {
      key: 'authDigestType',
      type: 'radio',
      defaultValue: FsAuthDigest.SHA256,
      templateOptions: {
        appearance: 'fill',
        label: t('cameras.authDigest'),
        options: [
          { label: NETWORK_PROTOCOL_SPECS.AUTH_DIGESTS.SHA256, value: FsAuthDigest.SHA256 },
          { label: NETWORK_PROTOCOL_SPECS.AUTH_DIGESTS.MD5, value: FsAuthDigest.MD5 },
        ],
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getTranscodeField(fieldConfig?: Partial<SnFormFieldConfig>): SnFormFieldConfig {
    const baseFieldConfig: SnFormFieldConfig = {
      key: 'transcode',
      type: 'drag-slide-toggle',
      templateOptions: {
        toggleLabel: t('cameras.transcode'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public isSubPathField(fieldConfig?: Partial<SnFormFieldConfig>): SnFormFieldConfig {
    const baseFieldConfig: SnFormFieldConfig = {
      key: 'isSubEnabled',
      type: 'drag-slide-toggle',
      templateOptions: {
        toggleLabel: t('cameras.secondaryPath'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getTranscodeSourceProtocol(fieldConfig?: Partial<SnFormFieldConfig>): SnFormFieldConfig {
    const baseFieldConfig: SnFormFieldConfig = {
      key: 'transcodeSourceProtocol',
      type: 'select',
      templateOptions: {
        label: t('cameras.sourceProtocol'),
        options: [
          { label: NETWORK_PROTOCOL_SPECS.PROTOCOLS.HTTP, value: SnCameraProtocol.HTTP },
          { label: NETWORK_PROTOCOL_SPECS.PROTOCOLS.HTTPS, value: SnCameraProtocol.HTTPS },
          { label: NETWORK_PROTOCOL_SPECS.PROTOCOLS.RTSP, value: SnCameraProtocol.RTSP },
          { label: NETWORK_PROTOCOL_SPECS.PROTOCOLS.RTMP, value: SnCameraProtocol.RTMP },
          { label: NETWORK_PROTOCOL_SPECS.PROTOCOLS.RTSPS, value: SnCameraProtocol.RTSPS },
          { label: NETWORK_PROTOCOL_SPECS.PROTOCOLS.UDP, value: SnCameraProtocol.UDP },
        ],
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getTranscodeCommand(fieldConfig?: Partial<SnFormFieldConfig>): SnFormFieldConfig {
    const baseFieldConfig: SnFormFieldConfig = {
      key: 'transcodeCommand',
      type: 'input',
      templateOptions: {
        label: t('cameras.command'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getSslField(fieldConfig?: Partial<SnFormFieldConfig>): SnFormFieldConfig {
    const baseFieldConfig: SnFormFieldConfig = {
      key: 'ssl',
      type: 'drag-slide-toggle',
      templateOptions: {
        toggleLabel: NETWORK_PROTOCOL_SPECS.PROTOCOLS.SSL,
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getRecordField(fieldConfig?: Partial<SnFormFieldConfig>): SnFormFieldConfig {
    const baseFieldConfig: SnFormFieldConfig = {
      key: 'record',
      type: 'drag-slide-toggle',
      templateOptions: {
        toggleLabel: t('cameras.recordVideo'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getLoopDurationField(fieldConfig?: Partial<SnFormFieldConfig>): SnFormFieldConfig {
    const baseFieldConfig: SnFormFieldConfig = {
      key: 'loopDuration',
      type: 'input',
      templateOptions: {
        type: 'number',
        label: t('cameras.loopDuration'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getUnmanagedTypeField(
    fieldConfig?: Partial<ISnSelectFormFieldConfig>,
  ): ISnSelectFormFieldConfig {
    const baseFieldConfig: ISnSelectFormFieldConfig = {
      key: 'unmanagedType',
      type: 'select',
      templateOptions: {
        label: t('common.type'),
        options: [
          { label: NETWORK_PROTOCOL_SPECS.MEDIA_FORMATS.JPEG, value: SnCameraUnmanagedType.JPEG },
          { label: NETWORK_PROTOCOL_SPECS.MEDIA_FORMATS.HLS, value: SnCameraUnmanagedType.HLS },
          { label: NETWORK_PROTOCOL_SPECS.MEDIA_FORMATS.SLDP, value: SnCameraUnmanagedType.SLDP },
        ],
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getUnmanagedUrField(
    fieldConfig?: Partial<ISnInputFormFieldConfig>,
  ): ISnInputFormFieldConfig {
    const baseFieldConfig: ISnInputFormFieldConfig = {
      key: 'unmanagedUrl',
      type: 'input',
      templateOptions: {
        label: t('cameras.url'),
        placeholder: t('cameras.streamUrl'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getRefreshTimeField(
    fieldConfig?: Partial<ISnSliderFormFieldConfig>,
  ): ISnSliderFormFieldConfig {
    const baseFieldConfig: ISnSliderFormFieldConfig = {
      key: 'refreshTime',
      type: 'slider',
      templateOptions: {
        label: t('cameras.refresh'),
        min: 1,
        max: 15,
        step: 1,
        thumbLabel: true,
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getExternalApplianceSelectField(
    fieldConfig?: Partial<ISnHttpSelectFormFieldConfig<SnExternalAppliance>>,
    options: ISnApplianceSelectFieldOptions = {},
  ): ISnHttpSelectFormFieldConfig<SnExternalAppliance> {
    const baseFieldConfig: ISnHttpSelectFormFieldConfig<SnExternalAppliance> = {
      key: 'externalAppliance',
      type: 'http-select',
      templateOptions: {
        label: t('cameras.externalAppliance'),
        placeholder: t('cameras.selectExternalAppliance'),
        search: true,
        searchParameter: 'search',
        valueProp: 'id',
        labelProp: 'name',
        infinityScroll: true,
        fetchDataFn: (fetchParams: ISnListQueryParameters) => {
          return this._externalAppliancesAPI
            .getExternalApplianceList({ ...options.params, ...fetchParams })
            .pipe(
              map((listResponse: any) => {
                if (options.exclude) {
                  const excludeList = castArray(options.exclude);
                  const filteredResult = listResponse.results.filter(
                    (appliance) => !excludeList.includes(appliance.id),
                  );
                  return {
                    ...listResponse,
                    results: filteredResult,
                  };
                }
                return listResponse;
              }),
            );
        },
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  public getExternalCameraSelectField(
    fieldConfig?: Partial<ISnSelectSearchFormFieldConfig>,
  ): ISnSelectSearchFormFieldConfig {
    const baseFieldConfig: ISnSelectSearchFormFieldConfig = {
      key: 'externalCameraId',
      type: 'select-search',
      templateOptions: {
        label: t('cameras.selectCamera'),
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }
}
